import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type TransactionTerminologyMode = 'credit_debit' | 'in_out';

export type TransactionTerminologyKey =
  | 'credit'
  | 'debit'
  | 'totalCredit'
  | 'totalDebit'
  | 'creditAmount'
  | 'debitAmount'
  | 'creditBalance'
  | 'debitBalance'
  | 'crDr'
  | 'creditShort'
  | 'debitShort';

interface StoredUser {
  dairies?: Array<{
    id?: number;
    configurations?: Record<string, any>;
    [key: string]: any;
  }>;
  d_id?: number;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class TransactionTerminologyService {
  private readonly STORAGE_PREFIX = 'crm-transaction-terminology';
  private readonly USER_KEY = 'milko_user';
  private readonly MEMBER_USER_KEY = 'milko_member_user';
  private readonly MEMBER_TOKEN_KEY = 'milko_member_jwt';

  constructor(private translate: TranslateService) {}

  get mode(): TransactionTerminologyMode {
    const configMode = this.normalizeMode(this.getConfigMode());
    if (configMode) {
      this.syncStoredMode(configMode);
      return configMode;
    }

    const stored = this.getStoredMode();
    if (stored) return stored;

    return 'credit_debit';
  }

  setMode(mode: TransactionTerminologyMode): void {
    const dairyId = this.getActiveDairyId();
    if (!dairyId) return;

    localStorage.setItem(this.storageKey(dairyId), mode);
    this.patchStoredUserConfig(mode);
  }

  label(key: TransactionTerminologyKey): string {
    const mode = this.mode;
    const map: Record<TransactionTerminologyKey, string> = {
      credit: mode === 'in_out' ? 'terminology.in' : 'terminology.credit',
      debit: mode === 'in_out' ? 'terminology.out' : 'terminology.debit',
      totalCredit: mode === 'in_out' ? 'terminology.total_in' : 'terminology.total_credit',
      totalDebit: mode === 'in_out' ? 'terminology.total_out' : 'terminology.total_debit',
      creditAmount: mode === 'in_out' ? 'terminology.in_amount' : 'terminology.credit_amount',
      debitAmount: mode === 'in_out' ? 'terminology.out_amount' : 'terminology.debit_amount',
      creditBalance: mode === 'in_out' ? 'terminology.in_balance' : 'terminology.credit_balance',
      debitBalance: mode === 'in_out' ? 'terminology.out_balance' : 'terminology.debit_balance',
      crDr: mode === 'in_out' ? 'terminology.in_out' : 'terminology.cr_dr',
      creditShort: mode === 'in_out' ? 'terminology.in_short' : 'terminology.credit_short',
      debitShort: mode === 'in_out' ? 'terminology.out_short' : 'terminology.debit_short',
    };

    const translationKey = map[key];
    const translated = this.translate.instant(translationKey);
    if (translated && translated !== translationKey && !String(translated).startsWith('terminology.')) {
      return translated;
    }

    return this.fallbackLabel(key, mode);
  }

  sideLabel(value: string | null | undefined, short = false): string {
    const normalized = String(value || '').toUpperCase().replace('.', '');
    if (normalized === 'CR' || normalized === 'CREDIT') {
      return short ? this.label('creditShort') : this.label('credit');
    }
    if (normalized === 'DR' || normalized === 'DEBIT') {
      return short ? this.label('debitShort') : this.label('debit');
    }
    return String(value || '');
  }

  private getStoredMode(): TransactionTerminologyMode | null {
    const dairyId = this.getActiveDairyId();
    if (!dairyId) return null;
    return this.normalizeMode(localStorage.getItem(this.storageKey(dairyId)));
  }

  private getConfigMode(): string {
    const user = this.readJson<StoredUser>(this.USER_KEY);
    const member = this.readJson<any>(this.MEMBER_USER_KEY);
    const memberMode = String(
      member?.transaction_terminology ||
      this.extractModeFromConfig(member?.configurations) ||
      ''
    );

    const userConfigMode = this.extractModeFromConfig(user?.dairies?.[0]?.configurations);

    // In member portal session, prefer member payload terminology.
    if (this.isMemberSession()) {
      return memberMode || userConfigMode || '';
    }

    return userConfigMode || memberMode || '';
  }

  private patchStoredUserConfig(mode: TransactionTerminologyMode): void {
    const user = this.readJson<StoredUser>(this.USER_KEY);
    if (!user?.dairies?.length) return;

    const updated = { ...user };
    const dairies = [...(updated.dairies || [])];
    const dairy = { ...dairies[0] };
    dairy.configurations = { ...(dairy.configurations || {}), transaction_terminology: mode };
    updated.dairies = [dairy, ...dairies.slice(1)];
    localStorage.setItem(this.USER_KEY, JSON.stringify(updated));
  }

  private getActiveDairyId(): number {
    const member = this.readJson<{ d_id?: number }>(this.MEMBER_USER_KEY);
    const user = this.readJson<StoredUser>(this.USER_KEY);

    if (this.isMemberSession()) {
      const memberDairyId = Number(member?.d_id || 0);
      if (memberDairyId) return memberDairyId;
    }

    const userDairyId = Number(user?.dairies?.[0]?.id || user?.d_id || 0);
    if (userDairyId) return userDairyId;

    return Number(member?.d_id || 0);
  }

  private storageKey(dairyId: number): string {
    return `${this.STORAGE_PREFIX}:${dairyId}`;
  }

  private normalizeMode(value: string | null | undefined): TransactionTerminologyMode | null {
    if (!value) return null;
    const normalized = String(value).trim().toLowerCase().replace(/[\s/-]+/g, '_');
    if (normalized === 'credit_debit') return 'credit_debit';
    if (normalized === 'in_out') return 'in_out';
    return null;
  }

  private fallbackLabel(key: TransactionTerminologyKey, mode: TransactionTerminologyMode): string {
    const creditDebit: Record<TransactionTerminologyKey, string> = {
      credit: 'Credit',
      debit: 'Debit',
      totalCredit: 'Total Credit',
      totalDebit: 'Total Debit',
      creditAmount: 'Credit Amount',
      debitAmount: 'Debit Amount',
      creditBalance: 'Credit Balance',
      debitBalance: 'Debit Balance',
      crDr: 'Cr/Dr',
      creditShort: 'Cr.',
      debitShort: 'Dr.',
    };
    const inOut: Record<TransactionTerminologyKey, string> = {
      credit: 'In',
      debit: 'Out',
      totalCredit: 'Total In',
      totalDebit: 'Total Out',
      creditAmount: 'In Amount',
      debitAmount: 'Out Amount',
      creditBalance: 'In Balance',
      debitBalance: 'Out Balance',
      crDr: 'In/Out',
      creditShort: 'In',
      debitShort: 'Out',
    };

    return (mode === 'in_out' ? inOut : creditDebit)[key];
  }

  private readJson<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) as T : null;
    } catch {
      return null;
    }
  }

  private syncStoredMode(mode: TransactionTerminologyMode): void {
    const dairyId = this.getActiveDairyId();
    if (!dairyId) return;

    const key = this.storageKey(dairyId);
    const existing = this.normalizeMode(localStorage.getItem(key));
    if (existing !== mode) {
      localStorage.setItem(key, mode);
    }
  }

  private isMemberSession(): boolean {
    return !!localStorage.getItem(this.MEMBER_TOKEN_KEY);
  }

  private extractModeFromConfig(config: any): string {
    if (!config) return '';

    if (Array.isArray(config)) {
      const row = config[0] || {};
      return String(row?.transaction_terminology || '');
    }

    return String(config?.transaction_terminology || '');
  }
}